import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { CheckMarkCircleIcon } from '@/UI/Icons';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { useDeleteSubflowTemplate } from '@/queries/useSubflowTemplates';
import { useGlobalStore } from '@/state/global.state';
import { useFetchMenuItems } from '@/hooks/useFetchMenuItems';
import { SubflowTemplateType } from '@/types/api/subflowTemplate';
import type { SubflowTemplateResponseDto } from '@/types/api/subflowTemplate';

const WA = I18N_KEYS.RECIPE_MAIN.FLOW.LEFT_PANEL.WORKSPACE_ASSETS;

export function useDeleteAssetConfirmation() {
  const { t } = useTranslation();
  const { openConfirmationDialog } = useConfirmationDialogStore();
  const { mutate: deleteTemplate } = useDeleteSubflowTemplate();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { fetchMenuItems } = useFetchMenuItems();
  const { getItem: getSuppressed, setItem: setSuppressed } = useLocalStorage<boolean>(
    'suppressDeleteAssetConfirmation',
  );

  const deleteAsset = useCallback(
    (asset: SubflowTemplateResponseDto) => {
      const isModel = asset.type === SubflowTemplateType.SavedModel;

      deleteTemplate(asset.id, {
        onSuccess: () => {
          void fetchMenuItems();
          updateSnackbarData({
            text: t(isModel ? WA.DELETE_MODEL_SUCCESS : WA.DELETE_NODE_SUCCESS),
            isOpen: true,
            icon: <CheckMarkCircleIcon width={20} height={20} />,
          });
        },
        onError: () => {
          updateSnackbarData({
            text: t(isModel ? WA.DELETE_MODEL_ERROR : WA.DELETE_NODE_ERROR),
            isOpen: true,
            severity: 'error',
          });
        },
      });
    },
    [deleteTemplate, fetchMenuItems, updateSnackbarData, t],
  );

  const confirmDelete = useCallback(
    (asset: SubflowTemplateResponseDto) => {
      if (getSuppressed()) {
        deleteAsset(asset);
        return;
      }

      openConfirmationDialog({
        title: t(WA.DELETE_TITLE, { name: asset.name }),
        message: t(WA.DELETE_MESSAGE),
        confirmBtnText: t(WA.DELETE_CONFIRM),
        checkboxLabel: t(WA.DELETE_DONT_SHOW),
        isCheckboxChecked: false,
        onConfirm: (checkboxChecked) => {
          setSuppressed(checkboxChecked ?? false);
          deleteAsset(asset);
        },
      });
    },
    [getSuppressed, setSuppressed, openConfirmationDialog, deleteAsset, t],
  );

  return confirmDelete;
}
